import React, { forwardRef, useId } from 'react';
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}
const sizeStyles = {
  sm: 'px-3 py-1.5 text-body-sm',
  md: 'px-4 py-2.5 text-body-md',
  lg: 'px-4 py-3 text-body-lg'
};
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  hint,
  size = 'md',
  leftIcon,
  rightIcon,
  fullWidth = true,
  disabled,
  className = '',
  id,
  ...props
}, ref) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  return <div className={`${fullWidth ? 'w-full' : ''}`} data-id="element-457">
        {label && <label htmlFor={inputId} className="block text-body-sm font-medium text-surface-700 mb-1.5" data-id="element-458">
            {label}
          </label>}
        <div className="relative" data-id="element-459">
          {leftIcon && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 pointer-events-none" data-id="element-460">
              {leftIcon}
            </div>}
          <input ref={ref} id={inputId} disabled={disabled} aria-invalid={!!error} aria-describedby={error ? errorId : hint ? hintId : undefined} className={`
              w-full rounded-input border bg-white
              transition-colors duration-150
              placeholder:text-surface-400
              ${sizeStyles[size]}
              ${leftIcon ? 'pl-10' : ''}
              ${rightIcon ? 'pr-10' : ''}
              ${error ? 'border-error-500 focus:border-error-500 focus:ring-2 focus:ring-error-100' : 'border-surface-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100'}
              ${disabled ? 'bg-surface-100 text-surface-500 cursor-not-allowed' : ''}
              ${className}
            `} {...props} data-id="element-461" />
          {rightIcon && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400" data-id="element-462">
              {rightIcon}
            </div>}
        </div>
        {error && <p id={errorId} className="mt-1.5 text-body-sm text-error-500" role="alert" data-id="element-463">
            {error}
          </p>}
        {hint && !error && <p id={hintId} className="mt-1.5 text-body-sm text-surface-500" data-id="element-464">
            {hint}
          </p>}
      </div>;
});
Input.displayName = 'Input';